import { useDropzone } from "react-dropzone";
import { Box, Typography, Button, IconButton } from "@mui/material";
import PropTypes from "prop-types";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";

function DragDropZoneMulti({
  label,
  required,
  files,
  setFiles,
  uploadInputRef,
  uploadedImages,
  setUploadedImages,
  selectedImageId,
  setSelectedImageId,
  disabled,
}) {
  if (disabled) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography fontSize={14} color="text.secondary">
          Không thể tải ảnh lên mặt manocanh.
        </Typography>
      </Box>
    );
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { "image/*": [] },
    noClick: true,
    noKeyboard: true,
    multiple: true,
  });

  const handleZoneClick = () => {
    uploadInputRef?.current?.click();
  };

  const removeFile = (index) => {
    setFiles((prev) => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  return (
    <Box>
      <Typography fontWeight="bold" fontSize="16px" mb={0.5} color="text.primary">
        {label} {required && <span style={{ color: "red" }}>*</span>}
      </Typography>

      <Box
        {...getRootProps()}
        sx={{
          border: "2px dashed #ccc",
          borderRadius: "8px",
          minHeight: 200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          cursor: "pointer",
          bgcolor: isDragActive ? "#f0f8ff" : "#fafafa",
          overflow: "hidden",
          position: "relative",
          textAlign: "center",
          p: 2,
        }}
        onDoubleClick={handleZoneClick}
      >
        <input {...getInputProps()} />
        <CloudUploadIcon fontSize="large" sx={{ color: "#888", mb: 1 }} />
        <Typography fontSize="14px" color="text.secondary" mb={1}>
          Kéo thả hoặc nhấp để tải lên
        </Typography>
        <Button
          variant="outlined"
          sx={{ border: "gray 1px solid", color: "black !important" }}
          onClick={(e) => {
            e.stopPropagation();
            uploadInputRef?.current?.click();
          }}
        >
          Chọn tệp
        </Button>
      </Box>

      {files.length > 0 && (
        <Box mt={2} display="flex" flexWrap="wrap" gap={1}>
          {files.map((f, index) => (
            <Box
              key={index}
              draggable
              sx={{
                position: "relative",
                width: 100,
                height: 100,
                borderRadius: 2,
                overflow: "hidden",
                border: "1px solid #ccc",
                cursor: "grab",
              }}
            >
              <Box
                component="img"
                src={f.preview}
                sx={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
              <IconButton
                size="small"
                onClick={() => removeFile(index)}
                sx={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  backgroundColor: "#fff",
                  border: "1px solid #aaa",
                  width: 20,
                  height: 20,
                  p: 0.5,
                }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      {uploadedImages.length > 0 && (
        <Box mt={3}>
          <Typography fontWeight="bold" fontSize={14} mb={1} sx={{ fontSize: "16px" }}>
            Ảnh đã thêm vào thiết kế:
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {uploadedImages.map((img) => (
              <Box
                key={img.id}
                onClick={() => setSelectedImageId?.(img.id)}
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: 2,
                  overflow: "hidden",
                  border: selectedImageId === img.id ? "2px solid #1976d2" : "1px solid #ccc",
                  position: "relative",
                  backgroundColor: selectedImageId === img.id ? "#e3f2fd" : "#fff",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  "&:hover": {
                    borderColor: "#1976d2",
                  },
                }}
              >
                <Box
                  component="img"
                  src={img.image?.src}
                  sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
                <IconButton
                  size="small"
                  onClick={() =>
                    setUploadedImages((prev) => prev.filter((item) => item.id !== img.id))
                  }
                  sx={{
                    position: "absolute !important",
                    top: 4,
                    right: 4,
                    width: "24px !important",
                    height: "24px !important",
                    borderRadius: "50% !important",
                    backgroundColor: "#fff",
                    border: "1px solid #aaa",
                    minWidth: 0, // ✅ quan trọng: xoá default minWidth gây méo
                    padding: 0, // ✅ xoá padding để icon không lệch
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 2,
                    boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
                    "&:hover": {
                      backgroundColor: "#fdd", // nhẹ nhàng khi hover
                    },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

DragDropZoneMulti.propTypes = {
  label: PropTypes.string,
  required: PropTypes.bool,
  files: PropTypes.arrayOf(
    PropTypes.shape({
      file: PropTypes.instanceOf(File),
      preview: PropTypes.string,
    })
  ).isRequired,
  setFiles: PropTypes.func.isRequired,
  uploadInputRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  uploadedImages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      image: PropTypes.instanceOf(Image),
      width: PropTypes.number,
      height: PropTypes.number,
    })
  ),
  setUploadedImages: PropTypes.func,
  selectedImageId: PropTypes.string,
  setSelectedImageId: PropTypes.func,
  disabled: PropTypes.bool,
};

DragDropZoneMulti.defaultProps = {
  label: "",
  required: false,
  uploadInputRef: null,
  uploadedImages: [],
  selectedImageId: null,
  setSelectedImageId: () => {},
  setUploadedImages: () => {},
  disabled: false,
};

export default DragDropZoneMulti;
